"use client";

import {
  useRef,
  useState,
  type ElementType,
  type ReactNode,
  type RefObject,
} from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import SplitType from "split-type";

gsap.registerPlugin(useGSAP, ScrollTrigger);

type AnimatedCopyProps = {
  children: ReactNode;
  className?: string;
  tag?: ElementType;
  delay?: number;
  duration?: number;
  ease?: string;
  stagger?: number;
  direction?: "top" | "bottom";
  /** false = play immediately on mount */
  animateOnScroll?: boolean;
  /** ScrollTrigger start, e.g. "top 80%" */
  start?: string;
  /** Use another element as the trigger (needed inside sticky/pinned parents) */
  triggerRef?: RefObject<HTMLElement | null>;
};

export default function AnimatedCopy({
  children,
  className = "",
  tag = "p",
  delay = 0,
  duration = 1,
  ease = "power4.out",
  stagger = 0.05,
  direction = "bottom",
  animateOnScroll = true,
  start = "top 80%",
  triggerRef,
}: AnimatedCopyProps) {
  const Tag = tag as ElementType;
  const ref = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);

  // SplitType rewrites the DOM that React owns. Remounting the element when the
  // text changes stops React from patching nodes that no longer exist.
  const textKey = typeof children === "string" ? children : "static";

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      // Reduced motion: show the text as-is, no splitting, no animation.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setReady(true);
        return;
      }

      let split: SplitType | null = null;
      let tl: gsap.core.Timeline | null = null;
      let resizeTimer: ReturnType<typeof setTimeout> | undefined;
      let lastWidth = el.offsetWidth;
      let played = false;
      let cancelled = false;

      const teardown = () => {
        tl?.scrollTrigger?.kill();
        tl?.kill();
        tl = null;
        split?.revert();
        split = null;
      };

      const build = () => {
        if (cancelled) return;
        teardown();

        split = new SplitType(el, { types: "lines", lineClass: "split-line" });

        const inners: HTMLElement[] = [];
        split.lines?.forEach((line) => {
          const inner = document.createElement("span");
          inner.className = "split-line-inner block will-change-transform";
          while (line.firstChild) inner.appendChild(line.firstChild);
          line.appendChild(inner);
          inners.push(inner);
        });

        if (!inners.length) {
          setReady(true);
          return;
        }

        // After a resize re-split, don't replay: just show the final state.
        if (played) {
          gsap.set(inners, { yPercent: 0 });
          return;
        }

        gsap.set(inners, { yPercent: direction === "top" ? -110 : 110 });
        setReady(true);

        tl = gsap.timeline({
          delay,
          scrollTrigger: animateOnScroll
            ? {
                trigger: triggerRef?.current ?? el,
                start,
                once: true,
              }
            : undefined,
          onComplete: () => {
            played = true;
          },
        });

        tl.to(inners, { yPercent: 0, duration, ease, stagger });
      };

      // Wait for fonts: line breaks are wrong if measured with the fallback font.
      document.fonts.ready.then(build);

      // Re-split only when the width changes (ignores mobile URL-bar height changes).
      const ro = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const w = el.offsetWidth;
          if (w === lastWidth) return;
          lastWidth = w;
          build();
          ScrollTrigger.refresh();
        }, 200);
      });
      ro.observe(el);

      return () => {
        cancelled = true;
        clearTimeout(resizeTimer);
        ro.disconnect();
        teardown();
      };
    },
    {
      scope: ref,
      dependencies: [
        textKey,
        direction,
        animateOnScroll,
        start,
        delay,
        duration,
        ease,
        stagger,
      ],
    },
  );

  return (
    <Tag
      key={textKey}
      ref={ref}
      className={[
        // hide until split is done, so there is no flash of unsplit text
        ready ? "" : "invisible",
        // line mask (replaces AnimatedCopy.css); padding stops ascenders/descenders being clipped
        "[&_.split-line]:m-0 [&_.split-line]:block [&_.split-line]:overflow-hidden",
        "[&_.split-line]:-mb-[0.12em] [&_.split-line]:pb-[0.12em]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </Tag>
  );
}